import { useState, useCallback, useContext, useRef } from 'react';
import { UserContext } from '../../../Contexts/UserContext';
import { screenerData } from '../../../Config/config';

export const DEFAULT_FILTERS = {
  tag: ['fintech'],
  latestRound: ['Pre-Seed', 'Seed'],
  revenueGrowth: { operator: 'gte', value: 30 },
};

export const DEFAULT_SCREENER_NAME = 'Seed-funded fintech cos with 30% revenue growth';

/**
 * Manages screener table data fetching, filters, pagination, and sort state.
 */
const useScreenerData = () => {
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState({ sort_by: 'latest_deal_date', sort_order: 'desc' });
  const [page, setPage] = useState(1);
  const [tableData, setTableData] = useState({ data: [], total_count: 0 });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [viewMode, setViewMode] = useState('filter');
  const [notificationCmpIds, setNotificationCmpIds] = useState(null);

  const { getToken } = useContext(UserContext);

  // Use refs to read latest sort/viewMode inside fetch without adding them as deps
  const sortByRef = useRef(sortBy);
  sortByRef.current = sortBy;
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;

  /** Check if any filter has a meaningful value */
  const hasActiveFilters = useCallback((f) => {
    return Object.values(f).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some((v) => v !== null && v !== '');
      }
      return value !== null && value !== '' && value !== undefined;
    });
  }, []);

  /**
   * Core fetch. Returns the response data or null.
   * @param {Object}  filtersToApply
   * @param {string|null} screenerId
   * @param {Array|null}  cmpIdsOverride - newly added cmp IDs from notification
   */
  const fetchTableData = useCallback(
    async (filtersToApply, screenerId = null, cmpIdsOverride = null) => {
      const cmpIds = cmpIdsOverride ?? notificationCmpIds;
      if (!hasActiveFilters(filtersToApply) && !screenerId && !cmpIds) return null;

      setFilters(filtersToApply);
      setLoading(true);

      try {
        const body = screenerId
          ? {
              screener_id: screenerId,
              ...(cmpIds ? { newly_added: cmpIds } : {}),
              ...sortByRef.current,
              list_type: viewModeRef.current,
              page: filtersToApply.page,
            }
          : { ...filtersToApply, ...sortByRef.current, list_type: viewModeRef.current };

        const response = await fetch(screenerData, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: getToken() },
          body: JSON.stringify(body),
        });

        const responseData = await response.json();
        setTableData(responseData);
        return responseData;
      } catch (error) {
        console.error('Error fetching screener data:', error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getToken, hasActiveFilters, notificationCmpIds],
  );

  /** Reset everything to blank slate */
  const resetData = useCallback(() => {
    setPage(1);
    setFilters({});
    setTableData({ data: [], total_count: 0 });
  }, []);

  /** Update a single page */
  const changePage = useCallback(
    (newPage, screenerId) => {
      setPage(newPage);
      fetchTableData({ ...filters, page: newPage }, screenerId);
    },
    [filters, fetchTableData],
  );

  /** Convenience: update table rows locally (e.g. after status change) */
  const updateRows = useCallback((companyIds, updater) => {
    setTableData((prev) => ({
      ...prev,
      data: prev.data?.map((row) => (companyIds.includes(row.company_id) ? updater(row) : row)),
    }));
  }, []);

  const markInitialLoadDone = useCallback(() => setInitialLoading(false), []);

  return {
    // State
    filters,
    setFilters,
    sortBy,
    setSortBy,
    page,
    setPage,
    tableData,
    loading,
    initialLoading,
    viewMode,
    setViewMode,
    notificationCmpIds,
    setNotificationCmpIds,

    // Actions
    hasActiveFilters,
    fetchTableData,
    resetData,
    changePage,
    updateRows,
    markInitialLoadDone,
  };
};

export default useScreenerData;
